import React, { useCallback, useEffect, useMemo, useState } from "react";

import { exportActionsHealthPdf, getActionsHealthSnapshot } from "../../api/actions.api.js";
import Loader from "../common/Loader";
import Button from "../../ui/Button";
import Table from "../../ui/Table";
import StatusBadge from "../../ui/StatusBadge";
import ActionsHealthPrintModal from "./print/ActionsHealthPrintModal";
import ActionsHealthPrintPreview from "./print/ActionsHealthPrintPreview";
import ActionsHealthPrintStyles from "./print/ActionsHealthPrintStyles";
import { colors, radius, shadows, spacing, typography } from "../../ui/tokens";
import { normalizeActionStatus } from "../../utils/actionStatus";
import {
  sectionHeaderStyle,
  surfaceCardBodyStyle,
  surfaceCardHeaderStyle,
  surfaceCardStyle,
  toolbarStyle,
} from "../../ui/pagePatterns";

const KPI_DEFINITIONS = [
  { key: "overdue", label: "Overdue", accessor: (data) => data?.overdue_actions || 0, tone: colors.danger },
  { key: "blocked", label: "Blocked", accessor: (data) => data?.blocked_actions || 0, tone: colors.warning },
  { key: "due_soon", label: "Due soon", accessor: (data) => data?.due_soon_actions || 0, tone: colors.accent },
  { key: "missing_owner", label: "Missing owner", accessor: (data) => data?.missing_owner || 0, tone: "#0ea5e9" },
  {
    key: "missing_assignee",
    label: "Missing assignee",
    accessor: (data) => data?.missing_assignee || 0,
    tone: "#6366f1",
  },
  {
    key: "high_priority_open",
    label: "High priority open",
    accessor: (data) => data?.high_priority_open || 0,
    tone: "#b45309",
  },
];

const isDisplayOption = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);
const ACTION_PRIORITY_VALUES = new Set(["LOW", "MEDIUM", "HIGH", "CRITICAL"]);

const getFilterPrimitiveValue = (value) => {
  if (!isDisplayOption(value)) {
    return value;
  }
  if (value.value !== undefined && value.value !== null) {
    return value.value;
  }
  return value.label;
};

const normalizeHealthStatusValue = (value) => {
  const normalized = normalizeActionStatus(getFilterPrimitiveValue(value));
  return normalized || undefined;
};

const normalizeHealthPriorityValue = (value) => {
  const raw = String(getFilterPrimitiveValue(value) || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return ACTION_PRIORITY_VALUES.has(raw) ? raw : undefined;
};

const normalizeOptionalFilterText = (value) => {
  const normalized = String(getFilterPrimitiveValue(value) ?? "").trim();
  return normalized || undefined;
};

const buildHealthRequestParams = (filters = {}) => {
  const params = {
    as_of: filters?.asOf,
  };
  const q = normalizeOptionalFilterText(filters?.search);
  const status = normalizeHealthStatusValue(filters?.status);
  const category = normalizeOptionalFilterText(filters?.category);
  const priority = normalizeHealthPriorityValue(filters?.priority);
  const owner = normalizeOptionalFilterText(filters?.owner);
  const assignee = normalizeOptionalFilterText(filters?.assignee);

  if (q) params.q = q;
  if (status) params.status = status;
  if (category) params.category = category;
  if (priority) params.priority = priority;
  if (owner) params.owner = owner;
  if (assignee) params.assignee = assignee;

  return params;
};

const getHealthErrorMessage = (err) => {
  const status = Number(err?.status || err?.response?.status || 0);
  if (status === 401) {
    return "You are not signed in.";
  }
  if (status === 403) {
    return "You do not have access to Actions Health.";
  }
  if (status === 422) {
    return "Actions Health could not be loaded because the request was invalid.";
  }
  return err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Unable to load actions health.";
};

const getDisplayText = (value, fallback = "-") => {
  const normalized = !isDisplayOption(value)
    ? String(value ?? "").trim()
    : typeof value.label === "string" && value.label.trim()
      ? value.label.trim()
      : typeof value.value === "string"
        ? value.value.trim()
        : "";
  return normalized || fallback;
};

const formatDate = (value) => {
  if (!value) return "Not set";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Not set";
  return parsed.toLocaleDateString();
};

const getDownloadFilename = (headers, fallbackName) => {
  const contentDisposition = headers?.["content-disposition"] || headers?.["Content-Disposition"] || "";
  const encodedNameMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (encodedNameMatch?.[1]) {
    try {
      return decodeURIComponent(encodedNameMatch[1].trim());
    } catch (_error) {
      return encodedNameMatch[1].trim();
    }
  }

  const filenameMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i);
  const resolvedName = filenameMatch?.[1] || filenameMatch?.[2];
  return resolvedName ? resolvedName.trim() : fallbackName;
};

const buildActiveFilterPills = (filters = {}, snapshot = null) => {
  const pills = [];
  if (filters?.asOf) pills.push(`As at ${formatDate(filters.asOf)}`);
  if (snapshot?.timezone) pills.push(`Timezone: ${snapshot.timezone}`);
  if (filters?.search) pills.push(`Search: ${getDisplayText(filters.search, "")}`);
  if (filters?.status) pills.push(`Status: ${getDisplayText(filters.status, "")}`);
  if (filters?.priority) pills.push(`Priority: ${getDisplayText(filters.priority, "")}`);
  if (filters?.category) pills.push(`Category: ${getDisplayText(filters.category, "")}`);
  if (filters?.owner) pills.push(`Owner: ${getDisplayText(filters.owner, "")}`);
  if (filters?.assignee) pills.push(`Assignee: ${getDisplayText(filters.assignee, "")}`);
  return pills;
};

export default function ActionsHealthPanel({
  active,
  filters,
  refreshNonce = 0,
  onRefresh,
  onOpenAction,
  filterContent,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printPreviewVisible, setPrintPreviewVisible] = useState(false);
  const [downloadingPrintPdf, setDownloadingPrintPdf] = useState(false);
  const [printError, setPrintError] = useState("");

  useEffect(() => {
    if (!active) return;
    let cancelled = false;

    const loadHealth = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getActionsHealthSnapshot(buildHealthRequestParams(filters));
        if (cancelled) return;
        setData(response?.data ?? response ?? null);
      } catch (err) {
        if (cancelled) return;
        setError(getHealthErrorMessage(err));
        setData(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadHealth();
    return () => {
      cancelled = true;
    };
  }, [active, filters, refreshNonce]);

  const kpis = useMemo(
    () =>
      KPI_DEFINITIONS.map((metric) => ({
        ...metric,
        value: metric.accessor(data),
      })),
    [data]
  );
  const activeFilterPills = useMemo(() => buildActiveFilterPills(filters, data), [data, filters]);
  const printOverflow = useMemo(
    () => ({
      likelyOverflow:
        (data?.attention_items?.length || 0) > 6
        || (data?.attention_by_owner?.length || 0) > 6
        || (data?.recent_changes?.length || 0) > 4,
    }),
    [data]
  );
  const handleOpenPrintModal = useCallback(() => {
    setPrintPreviewVisible(false);
    setPrintError("");
    setPrintModalOpen(true);
  }, []);
  const handlePreviewPrint = useCallback(() => {
    setPrintPreviewVisible(true);
  }, []);
  const handlePrint = useCallback(() => {
    if (!data) return;
    setPrintPreviewVisible(true);
    setDownloadingPrintPdf(true);
    setPrintError("");

    exportActionsHealthPdf({
      snapshot: data,
      filters: filters || {},
    })
      .then((response) => {
        const pdfBlob =
          response?.data instanceof Blob ? response.data : new Blob([response?.data ?? response], { type: "application/pdf" });
        const downloadName = getDownloadFilename(response?.headers, "actions-health-one-page.pdf");
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        setPrintError(err?.response?.data?.detail || err?.message || "Unable to download actions health PDF.");
      })
      .finally(() => {
        setDownloadingPrintPdf(false);
      });
  }, [data, filters]);

  const attentionColumns = useMemo(
    () => [
      {
        id: "action",
        header: "Action",
        render: (row) => (
          <div style={{ display: "grid", gap: 4 }}>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onOpenAction?.(row.action_id);
              }}
              style={linkButtonStyle}
            >
              {row.action_title}
            </button>
            <span style={{ fontSize: typography.size.xs, color: colors.textMuted }}>{row.category}</span>
          </div>
        ),
      },
      {
        id: "reason",
        header: "Reason",
        render: (row) => (
          <div style={{ display: "flex", flexWrap: "wrap", gap: spacing.xs }}>
            {(row.reasons || []).map((reason) => (
              <span key={reason} style={reasonChipStyle}>
                {reason}
              </span>
            ))}
          </div>
        ),
      },
      {
        id: "ownership",
        header: "Owner / Assignee",
        render: (row) => (
          <div style={{ display: "grid", gap: 4 }}>
            <span>{row.owner}</span>
            <span style={{ fontSize: typography.size.xs, color: colors.textMuted }}>Assignee: {row.assignee}</span>
          </div>
        ),
      },
      {
        id: "status",
        header: "Status / Priority",
        render: (row) => (
          <div style={{ display: "grid", gap: 4 }}>
            <StatusBadge variant="status" value={row.status} fallbackLabel={row.status || "Unknown"} />
            <span style={{ fontSize: typography.size.xs, color: colors.textMuted }}>Priority: {row.priority}</span>
          </div>
        ),
      },
      {
        id: "due",
        header: "Due",
        accessor: (row) => (row.due_date ? formatDate(row.due_date) : "No due date"),
      },
      {
        id: "action_button",
        header: "Action",
        render: (row) => (
          <Button
            type="button"
            variant="secondary"
            onClick={(event) => {
              event.stopPropagation();
              onOpenAction?.(row.action_id);
            }}
          >
            Open
          </Button>
        ),
      },
    ],
    [onOpenAction]
  );

  return (
    <div style={{ display: "grid", gap: spacing.xl }}>
      <div style={surfaceCardStyle}>
        <div style={surfaceCardHeaderStyle}>
          <div style={sectionHeaderStyle}>
            <div>
              <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>Actions health</div>
              <div style={{ fontWeight: typography.weight.medium, fontSize: 16, color: colors.text }}>
                What needed attention at this point
              </div>
            </div>
            <div style={{ fontSize: typography.size.xs, color: colors.textSubtle }}>
              {data?.timezone ? `Historical snapshot in ${data.timezone}.` : "Showing metrics for actions matching current filters."}
            </div>
          </div>
          <div style={toolbarStyle}>
            <Button
              type="button"
              variant="secondary"
              onClick={handleOpenPrintModal}
              disabled={loading || !data || Boolean(error)}
            >
              Print actions health to one page
            </Button>
            <Button type="button" variant="secondary" onClick={() => onRefresh && onRefresh()}>
              Refresh health
            </Button>
          </div>
        </div>

        {filterContent && <div style={healthFilterWrapStyle}>{filterContent}</div>}

        <div style={{ ...surfaceCardBodyStyle, display: "grid", gap: spacing.xl }}>
          {loading && <Loader label="Loading actions health..." />}
          {!loading && error && <div style={errorBannerStyle}>{error}</div>}

          {!loading && !error && data && (
            <>
              {activeFilterPills.length > 0 && (
                <div style={activeFilterWrapStyle}>
                  {activeFilterPills.map((pill) => (
                    <span key={pill} style={activeFilterChipStyle}>
                      {pill}
                    </span>
                  ))}
                </div>
              )}

              <div style={{ display: "grid", gap: spacing.xs }}>
                <div style={{ fontSize: typography.size.sm, color: colors.text }}>{data.scope_note}</div>
                {data.completeness_warning && <div style={warningBannerStyle}>{data.completeness_warning}</div>}
                {data.limitation_note && (
                  <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>{data.limitation_note}</div>
                )}
              </div>

              {data.total_actions === 0 && (
                <div style={emptyPanelStyle}>
                  <div style={{ fontWeight: typography.weight.medium }}>No actions match the current filters</div>
                  <div style={{ fontSize: typography.size.sm, color: colors.textSubtle }}>
                    Adjust the health filters to review a different slice of the action register.
                  </div>
                </div>
              )}

              {data.total_actions > 0 && (
                <>
                  <div style={kpiGridStyle}>
                    {kpis.map((metric) => (
                      <div key={metric.key} style={kpiCardStyle}>
                        <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>{metric.label}</div>
                        <div style={{ fontSize: 28, fontWeight: typography.weight.bold, color: metric.tone }}>{metric.value}</div>
                        <div style={{ fontSize: typography.size.xs, color: colors.textSubtle }}>
                          {metric.value ? "Matching actions need follow-up at the selected cutoff" : "No matching actions flagged at the selected cutoff"}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: "grid", gap: spacing.md }}>
                    <div style={sectionHeaderStyle}>
                      <div>
                        <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>Needs attention</div>
                        <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>
                          Prioritized by overdue and blocked work first, then ownership gaps and urgent priorities.
                        </div>
                      </div>
                      <div style={{ fontSize: typography.size.xs, color: colors.textSubtle }}>
                        {data.attention_actions} action{data.attention_actions === 1 ? "" : "s"} needed attention
                      </div>
                    </div>

                    {Array.isArray(data.attention_items) && data.attention_items.length > 0 ? (
                      <Table
                        columns={attentionColumns}
                        data={data.attention_items}
                        getRowKey={(row) => row.action_id}
                        onRowClick={(row) => onOpenAction?.(row.action_id)}
                        emptyState="No actions needed attention."
                      />
                    ) : (
                      <div style={emptyPanelStyle}>
                        <div style={{ fontWeight: typography.weight.medium }}>No actions needed attention</div>
                        <div style={{ fontSize: typography.size.sm, color: colors.textSubtle }}>
                          Current filters show no overdue, blocked, unassigned, or urgent open actions at the selected cutoff.
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={breakdownShellStyle}>
                    <div style={sectionHeaderStyle}>
                      <div>
                        <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>Attention by owner</div>
                        <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>
                          Owners with the largest action-health workload under the selected snapshot.
                        </div>
                      </div>
                    </div>
                    {Array.isArray(data.attention_by_owner) && data.attention_by_owner.length > 0 ? (
                      <div style={{ display: "grid", gap: spacing.sm }}>
                        {data.attention_by_owner.map((bucket) => {
                          const share =
                            data.attention_actions > 0 ? Math.round((bucket.count / data.attention_actions) * 100) : 0;
                          return (
                            <div key={bucket.owner} style={ownerBreakdownButtonStyle}>
                              <div style={ownerBreakdownLabelStyle}>
                                <span>{bucket.owner}</span>
                                <span style={{ color: colors.textMuted }}>
                                  {bucket.count} ({share}%)
                                </span>
                              </div>
                              <div style={ownerTrackStyle}>
                                <div style={{ ...ownerFillStyle, width: `${share}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ fontSize: typography.size.sm, color: colors.textSubtle }}>
                        No owner breakdown is available because no actions needed attention at the selected cutoff.
                      </div>
                    )}
                  </div>

                  {Array.isArray(data.recent_changes) && data.recent_changes.length > 0 && (
                    <div style={secondarySectionStyle}>
                      <div style={sectionHeaderStyle}>
                        <div>
                          <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>Recent changes</div>
                          <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>
                            Changes within the configured historical window ending at the selected cutoff.
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "grid", gap: spacing.sm }}>
                        {data.recent_changes.map((item) => (
                          <button
                            key={item.action_id}
                            type="button"
                            onClick={() => onOpenAction?.(item.action_id)}
                            style={recentChangeButtonStyle}
                          >
                            <div style={{ display: "grid", gap: 4, textAlign: "left" }}>
                              <span style={{ fontWeight: typography.weight.medium, color: colors.text }}>{item.action_title}</span>
                              <span style={{ fontSize: typography.size.xs, color: colors.textMuted }}>
                                {item.owner} | {item.priority} | Updated {formatDate(item.updated_at)}
                              </span>
                            </div>
                            <StatusBadge
                              variant="status"
                              value={item.status}
                              fallbackLabel={item.status || "Unknown"}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
      <ActionsHealthPrintModal
        open={printModalOpen}
        data={data}
        filters={filters}
        previewVisible={printPreviewVisible}
        overflow={printOverflow}
        printing={downloadingPrintPdf}
        downloadError={printError}
        onCancel={() => setPrintModalOpen(false)}
        onPreview={handlePreviewPrint}
        onPrint={handlePrint}
      />
      {data && (
        <>
          <ActionsHealthPrintStyles />
          <ActionsHealthPrintPreview
            data={data}
            filters={filters}
            root
            testId="actions-health-print-preview-root"
          />
        </>
      )}
    </div>
  );
}

const healthFilterWrapStyle = {
  ...surfaceCardBodyStyle,
  borderTop: `1px solid ${colors.border}`,
  borderBottom: `1px solid ${colors.border}`,
  padding: spacing.xl * 2,
  background: colors.surfaceSubtle,
};

const activeFilterWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.xs,
};

const activeFilterChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  border: `1px solid ${colors.border}`,
  padding: "4px 10px",
  fontSize: typography.size.xs,
  background: colors.surfaceSubtle,
  color: colors.text,
};

const kpiGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: spacing.md,
};

const kpiCardStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  background: colors.surface,
  boxShadow: shadows.xs,
  padding: spacing.xl,
  display: "grid",
  gap: spacing.xs,
  textAlign: "left",
};

const breakdownShellStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  background: colors.surface,
  boxShadow: shadows.xs,
  padding: spacing.xl,
  display: "grid",
  gap: spacing.md,
};

const ownerBreakdownButtonStyle = {
  display: "grid",
  gap: spacing.xs,
};

const ownerBreakdownLabelStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.sm,
  color: colors.text,
  fontSize: typography.size.sm,
};

const ownerTrackStyle = {
  width: "100%",
  height: 10,
  borderRadius: 999,
  background: colors.surfaceMuted,
  overflow: "hidden",
};

const ownerFillStyle = {
  height: "100%",
  borderRadius: 999,
  background: colors.accent,
};

const secondarySectionStyle = {
  borderTop: `1px solid ${colors.border}`,
  paddingTop: spacing.xl,
  display: "grid",
  gap: spacing.md,
};

const recentChangeButtonStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  background: colors.surface,
  padding: spacing.md,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.md,
  cursor: "pointer",
};

const emptyPanelStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  background: colors.surfaceSubtle,
  padding: spacing.xl,
  display: "grid",
  gap: spacing.xs,
};

const errorBannerStyle = {
  border: `1px solid ${colors.dangerBorder}`,
  background: colors.dangerSubtle,
  color: colors.danger,
  borderRadius: radius.md,
  padding: `${spacing.sm}px ${spacing.md}px`,
  fontSize: typography.size.xs,
};

const warningBannerStyle = {
  border: `1px solid ${colors.warningBorder}`,
  background: colors.warningSubtle,
  color: colors.warning,
  borderRadius: radius.md,
  padding: `${spacing.sm}px ${spacing.md}px`,
  fontSize: typography.size.xs,
};

const reasonChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  border: `1px solid ${colors.border}`,
  padding: "2px 8px",
  fontSize: typography.size.xs,
  background: colors.surfaceSubtle,
  color: colors.text,
};

const linkButtonStyle = {
  border: "none",
  background: "transparent",
  color: colors.accent,
  padding: 0,
  margin: 0,
  textAlign: "left",
  cursor: "pointer",
  fontSize: typography.size.sm,
  fontWeight: typography.weight.medium,
};
